import { AspObject } from './AspObject';
import { DisplayObject } from './DisplayObject';
import { IObjectsDrawer } from './interfaces';
import * as utils from '../common/utils';
import * as settings from '../common/settings';
import { AcousticVectorObject } from './AcousticVectorObject';
import { AppConfig } from '../app.config';
import { userSettings } from '../common/settings';
import * as aspSettings from '../asp/aspSettings';
import { Subscription, interval, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export enum AcousticVectorDataType {
  AcousticVector = 'acousticvector',
  AcousticVectorBack = 'acousticvectorback',
  AcousticVectorText = 'acousticvectortext',
  AcousticVectorHistory = 'acousticvectorhistory',
}

export class AcousticVectorDisplayObject extends DisplayObject {
  public readonly MAX_HISTORY = 10;
  private destroyed: boolean = false;
  private interval: any;
  private objRejected: boolean = false;

  AcousticVectorTextColor = [1, 1, 1, 1];
  AcousticVectorOpacity: number;
  globalOpacity: any;
  acousticLifeTime: number;
  symSize: number;

  constructor(aspObject: AspObject, drawer: IObjectsDrawer) {
    super(aspObject, drawer);
    this.AcousticVectorTextColor = utils.cssColorToNewmi(settings.userSettings.acousticTextColor);
    this.globalOpacity = settings.userSettings.nainatOpacity;
    this.AcousticVectorOpacity = settings.userSettings.acousticOpacity;
    this.symSize = settings.userSettings.symbolsSize;
    this.acousticLifeTime = settings.userSettings.acousticLifeTime;
    userSettings.secInter.subscribe(() => {
      this.dataBlockTimeUpdate();
    })
  }

  onDestroy() {
    this.destroyed = true;
    clearInterval(this.interval);
  }

  // should go and check this functions...checkDegInrange, checkFrequencyInRange, life time and other filters... plus maybe validity o
  isVisible(): boolean {
    if (!settings.userSettings.filterOn[settings.DisplayFilter.AcousticVector])
      return false;
    return true;
  }

  dataBlockTimeUpdate() {
    if (this.destroyed)
      return;

    let vector = this.aspObject as AcousticVectorObject;

    if (!userSettings.acousticSourcesOn[vector.Data.source - 1]) {
      this.rejectvector(vector);
      return;
    }

    if (this.drawData[AcousticVectorDataType.AcousticVectorText] != null) {
      let elap = new Date().getTime() - vector.Data.arrivalDate.getTime();
      let cls = 'unknown'; 
      if (settings.userSettings.acousticUseWhiteListOn) {
        // cls = this.checkFrequencyInRange(vector.Data.f0);
        cls = vector.Data.cls;
        if (cls == '') {
          this.rejectvector(vector);
        }
      }
      vector.Data.text.value = `ID: ${vector.Data.ID} \nCLS: ${cls}\nUT: ${(elap / 1000).toFixed(0)}`;
      vector.Data.text.size = this.drawer.getMapMngr().getSizeByZoomValue();
      this.drawData[AcousticVectorDataType.AcousticVectorText] = vector.Data.text;
      this.drawer.updateObjectInDm(this, [AcousticVectorDataType.AcousticVectorText], [AcousticVectorDataType.AcousticVectorText]);
    }
  }
  public firstTimeDraw: boolean = true;

  onDataChange(): void {
    if (this.destroyed)
      return;

    let vector: AcousticVectorObject = this.aspObject as AcousticVectorObject;
    if (!vector)
      return;

    let spColor = utils.cssColorToNewmi(userSettings.acousticSelectedColor);
    let textColor = [this.AcousticVectorTextColor[0], this.AcousticVectorTextColor[1], this.AcousticVectorTextColor[2], this.AcousticVectorOpacity];
    if (!this.checkDegInrange(vector.Data.Angle)) {
      this.rejectvector(vector);
      return;
    }
    let cls = 'unknown'; 
    if (settings.userSettings.acousticUseWhiteListOn) {
      // cls = this.checkFrequencyInRange(vector.Data.f0);
      cls = vector.Data.cls;
      if (cls == '') {
        this.rejectvector(vector);
      }
    }

    let elap = (Date.now() - vector.Data.arrivalDate.getTime()) / 1000;
    if (elap > userSettings.acousticLifeTime) {
      this.rejectvector(vector);
      return;
    }

    if (!userSettings.acousticSourcesOn[vector.Data.source - 1]) {
      this.rejectvector(vector);
      return;
    }

    let lineColor = vector.HistoryOn || aspSettings.selectedIds.has(`acoustic ${vector.Data.ID}`) ? spColor : vector.Data.lineColor;
    lineColor[3] = this.AcousticVectorOpacity * 0.6;
    this.drawData[AcousticVectorDataType.AcousticVector] = this.createGeometryDrawData(vector.Data.line, 3, lineColor);
    this.drawData[AcousticVectorDataType.AcousticVectorBack] = this.createGeometryDrawData(vector.Data.line, 6, [0, 0, 0, this.AcousticVectorOpacity * 0.7]);
    vector.Data.text.value = `ID: ${vector.Data.ID} \nCLS: ${cls}\nUT: ${(elap / 1000).toFixed(0)}`;

    vector.Data.text.color = textColor;
    // vector.Data.text.size = userSettings.symbolsSize; // need to do the calc here...
    vector.Data.text.size = this.drawer.getMapMngr().getSizeByZoomValue();
    this.drawData[AcousticVectorDataType.AcousticVectorText] = vector.Data.text;
    this.drawer.updateObjectInDm(this, [AcousticVectorDataType.AcousticVector], [AcousticVectorDataType.AcousticVector]);
    this.drawer.updateObjectInDm(this, [AcousticVectorDataType.AcousticVectorBack], [AcousticVectorDataType.AcousticVectorBack]);
    this.drawer.updateObjectInDm(this, [AcousticVectorDataType.AcousticVectorText], [AcousticVectorDataType.AcousticVectorText]);

    if (vector.HistoryOn) {
      while (vector.History.length > this.MAX_HISTORY){
        this.removeAcousticVectorHistory(vector.History[0].ID);
        vector.History.shift();
        continue;
      }

      let opacityDec = 1 / vector.History.length;
      let opacity = opacityDec;
      let color = utils.cssColorToNewmi(userSettings.acousticSelectedColor);
      for (let j = 0; j < vector.History.length; j++) {
        let histVector = vector.History[j];
        const uniqueKey = AcousticVectorDataType.AcousticVectorHistory + '|' + histVector.ID;
        if (!histVector)
          continue;
        color[3] = opacity;
        if (this.drawData[uniqueKey] == null) {
          this.drawData[uniqueKey] = this.createGeometryDrawData(histVector.line, 2, color);
          this.drawer.addObjectToDm(this, [uniqueKey], [AcousticVectorDataType.AcousticVectorHistory]);
        }
        else {
          this.drawData[uniqueKey] = this.createGeometryDrawData(histVector.line, 2, color);
          this.drawer.updateObjectInDm(this, [uniqueKey], [AcousticVectorDataType.AcousticVectorHistory]);
        }
        opacity += opacityDec;
      }
    }
    else if (vector.HistoryDeleted == false) {
      vector.HistoryDeleted = true;
      for (let j = 0; j < vector.History.length; j++) {
        this.removeAcousticVectorHistory(vector.History[j].ID);
      }
      vector.History = [];
    }

    if (this.firstTimeDraw || this.objRejected) {
      this.firstTimeDraw = false;
      this.objRejected = false;
      this.drawer.addObjectToDm(this, [AcousticVectorDataType.AcousticVector], [AcousticVectorDataType.AcousticVector]);
      this.drawer.addObjectToDm(this, [AcousticVectorDataType.AcousticVectorBack], [AcousticVectorDataType.AcousticVectorBack]);
      this.drawer.addObjectToDm(this, [AcousticVectorDataType.AcousticVectorText], [AcousticVectorDataType.AcousticVectorText]);
    }
    super.onDataChange();
  }

  private removeAcousticVectorHistory(id: string){
    const vectorKey = AcousticVectorDataType.AcousticVectorHistory + '|' + id;
    this.drawer.removeObjectFromDm(this, [vectorKey], [AcousticVectorDataType.AcousticVectorHistory]);
    this.drawData[vectorKey] = null;
    delete this.drawData[vectorKey]
  }

  checkDegInrange(deg: number): boolean {
    if (deg >= (this.aspObject as AcousticVectorObject).SnsObject.AcousticMinAngle && deg <= (this.aspObject as AcousticVectorObject).SnsObject.AcousticMaxAngle)
      return true;
    return false;
  }

  checkFrequencyInRange(freq: number): string {
    let arr = AppConfig.settings.AcousticLinesRangeWhiteList;
    if (!arr)
      return '';
    let str = '';
    for (var i = 0; i < arr.length; i++) {
      if (freq <= arr[i].MaxVal && freq >= arr[i].MinVal) {
        if (arr[i].Classification == '' || arr[i].Classification == null || arr[i].Classification == undefined)
          continue;
        str += arr[i].Classification + ' ';
      }
    }
    return str;
  }

  rejectvector(vector) {
    this.objRejected = true;
    this.drawData[AcousticVectorDataType.AcousticVector] = null;
    this.drawData[AcousticVectorDataType.AcousticVectorBack] = null;
    this.drawData[AcousticVectorDataType.AcousticVectorText] = null;
    this.drawer.removeObjectFromDm(this, [AcousticVectorDataType.AcousticVector], [AcousticVectorDataType.AcousticVector]);
    this.drawer.removeObjectFromDm(this, [AcousticVectorDataType.AcousticVectorBack], [AcousticVectorDataType.AcousticVectorBack]);
    this.drawer.removeObjectFromDm(this, [AcousticVectorDataType.AcousticVectorText], [AcousticVectorDataType.AcousticVectorText]);
    if (vector.HistoryOn) {
      for (let j = 0; j < vector.History.length; j++) {
        this.removeAcousticVectorHistory(vector.History[j].ID);
      }
    }
  }

  prepareToDraw(): void {
    super.prepareToDraw();

    let vector = this.aspObject as AcousticVectorObject;
    let elapsed = new Date().getTime() - vector.Data.arrivalDate.getTime();
    if (elapsed > 100 * 1000) {
      this.onDestroy();
      this.rejectvector(vector);
      vector.objectsPool.deleteObject(vector);
      return;
    }

    let acousticTextColor = utils.cssColorToNewmi(settings.userSettings.acousticTextColor);
    if (this.globalOpacity != settings.userSettings.nainatOpacity ||
      this.AcousticVectorTextColor.toString() != acousticTextColor.toString() ||
      this.AcousticVectorOpacity != settings.userSettings.acousticOpacity ||
      this.symSize != settings.userSettings.symbolsSize ||
      this.acousticLifeTime != settings.userSettings.acousticLifeTime) {
      this.symSize = settings.userSettings.symbolsSize;
      this.globalOpacity = settings.userSettings.nainatOpacity;
      this.AcousticVectorTextColor = utils.cssColorToNewmi(settings.userSettings.acousticTextColor);
      this.AcousticVectorOpacity = settings.userSettings.pointOpacity * settings.userSettings.acousticOpacity;
      this.acousticLifeTime = settings.userSettings.acousticLifeTime;
      this.onDataChange();
    }
    return;
  }
}
